import type { LLMModel } from "@pick-three-ai/constants";
import { MODEL_METADATA, LLM_PROVIDERS } from "@pick-three-ai/constants";
import { ProviderIcon } from "./ProviderIcon";
import { ChevronDown, Plus } from "lucide-react";
import { analytics } from "../lib/analytics";

type RankPosition = 1 | 2 | 3;

interface RankingSlotProps {
  rank: RankPosition;
  /** The model name (AI mode) or custom text entry (generic mode) */
  model: string | null;
  blurb: string;
  onModelClick: () => void;
  onModelTextChange?: (text: string) => void;
  onBlurbChange: (blurb: string) => void;
  isActive?: boolean;
  isReadOnly?: boolean;
  isGeneric?: boolean;
}

const MAX_MODEL_TEXT_LENGTH = 50;

const RANK_STYLES: Record<
  RankPosition,
  { glow: string; number: string; suffix: string; badgeClass: string }
> = {
  1: {
    glow: "glow-rank-1",
    number: "1",
    suffix: "st",
    badgeClass: "rank-badge-1",
  },
  2: {
    glow: "glow-rank-2",
    number: "2",
    suffix: "nd",
    badgeClass: "rank-badge-2",
  },
  3: {
    glow: "glow-rank-3",
    number: "3",
    suffix: "rd",
    badgeClass: "rank-badge-3",
  },
};

const MAX_BLURB_LENGTH = 60;

export function RankingSlot({
  rank,
  model,
  blurb,
  onModelClick,
  onModelTextChange,
  onBlurbChange,
  isActive = false,
  isReadOnly = false,
  isGeneric = false,
}: RankingSlotProps) {
  const styles = RANK_STYLES[rank];
  // In generic mode, model is free-form text so there's no provider
  // In AI mode, look up the provider from model metadata
  const isValidAIModel = !isGeneric && model && model in MODEL_METADATA;
  const provider = isValidAIModel ? MODEL_METADATA[model as LLMModel].provider : null;
  const providerInfo = provider ? LLM_PROVIDERS[provider] : null;
  const isHero = rank === 1;

  const handleBlurbBlur = () => {
    if (blurb.trim()) {
      analytics.blurbEntered(rank, blurb.trim().length);
    }
  };

  const handleModelTextBlur = () => {
    if (model?.trim()) {
      analytics.modelSelected(rank, model.trim());
    }
  };

  // Generic mode: render text input for free-form entry
  if (isGeneric) {
    return (
      <div
        className={`
          relative w-full
          rounded-xl
          glass transition-all duration-200
          ${model?.trim() ? styles.glow : ""}
        `}
      >
        {/* Generic Text Entry */}
        <div
          className={`
            group w-full
            flex items-center gap-2.5 sm:gap-3
            ${isHero ? "px-3 sm:px-4 py-3.5 sm:py-4" : "px-2.5 sm:px-3 py-2.5 sm:py-3"}
            rounded-xl
          `}
        >
          {/* Rank Badge */}
          <div
            className={`
              flex-shrink-0 flex items-center justify-center 
              ${isHero ? "w-12 h-12 sm:w-14 sm:h-14 text-base sm:text-lg" : "w-10 h-10 sm:w-11 sm:h-11 text-sm sm:text-base"}
              rounded-lg
              font-bold
              ${styles.badgeClass}
            `}
          >
            <span className="flex items-start">
              {styles.number}
              <sup className="text-[0.75em] ml-[0.05em] mt-[1em] font-bold">{styles.suffix}</sup>
            </span>
          </div>

          {/* Text Entry Content */}
          <div className="flex-1 min-w-0">
            {isReadOnly ? (
              <div className={`font-bold text-foreground truncate tracking-tight ${isHero ? "text-lg sm:text-xl" : "text-base sm:text-lg"}`}>
                {model || "—"}
              </div>
            ) : (
              <input
                type="text"
                value={model || ""}
                onChange={(e) => onModelTextChange?.(e.target.value)}
                onBlur={handleModelTextBlur}
                placeholder={`Enter your ${rank === 1 ? "top" : rank === 2 ? "second" : "third"} pick`}
                maxLength={MAX_MODEL_TEXT_LENGTH}
                className={`
                  w-full bg-transparent border-none outline-none
                  font-bold text-foreground placeholder:text-white/40
                  tracking-tight
                  ${isHero ? "text-lg sm:text-xl" : "text-base sm:text-lg"}
                `}
              />
            )}
          </div>
        </div>

        {/* Blurb Input - Only shown when text entry is non-empty */}
        {model?.trim() && (
          <div 
            className={`${isHero ? "px-3 sm:px-4 pb-3" : "px-2.5 sm:px-3 pb-2.5"} pt-1`}
            data-hide-on-export={!blurb ? "true" : undefined}
          >
            {/* Input for editing - hidden on export and in read-only mode */}
            {!isReadOnly && (
              <div className="flex items-center justify-center gap-1" data-hide-on-export="true">
                <span className="flex-shrink-0 font-serif text-white/50 text-2xl" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>"</span>
                <input
                  type="text"
                  value={blurb}
                  onChange={(e) => onBlurbChange(e.target.value)}
                  onBlur={handleBlurbBlur}
                  placeholder={`Why ${model}?`}
                  maxLength={MAX_BLURB_LENGTH}
                  className="flex-1 min-w-0 py-1 text-white/90 font-medium italic text-center placeholder:text-white/55 placeholder:font-medium bg-transparent border-0 border-b border-white/15 focus:border-white/40 focus:outline-none transition-colors text-base sm:text-sm"
                />
                <span className="flex-shrink-0 font-serif text-white/50 text-2xl" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>"</span>
              </div>
            )}
            {/* Static text for read-only mode and export - only visible when there's a blurb */}
            {blurb && (
              <p 
                className={`italic font-medium text-white/80 text-center text-sm ${isReadOnly ? "" : "hidden"}`}
                data-show-on-export="true"
              >
                <span className="font-serif" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>"</span>
                {blurb}
                <span className="font-serif" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>"</span>
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // AI mode: render model selector button
  return (
    <div
      className={`
        relative w-full
        rounded-xl
        glass transition-all duration-200
        ${isActive ? "ring-2 ring-accent/50 bg-[rgba(255,255,255,0.06)]" : ""}
        ${model ? styles.glow : ""}
      `}
    >
      {/* Model Selection Button (or div in read-only mode) */}
      <div
        role={isReadOnly ? undefined : "button"}
        tabIndex={isReadOnly ? undefined : 0}
        onClick={isReadOnly ? undefined : onModelClick}
        onKeyDown={isReadOnly ? undefined : (e) => e.key === 'Enter' && onModelClick()}
        className={`
          group w-full
          flex items-center gap-2.5 sm:gap-3
          ${isHero ? "px-3 sm:px-4 py-3.5 sm:py-4" : "px-2.5 sm:px-3 py-2.5 sm:py-3"}
          rounded-xl
          transition-all duration-200
          ${isReadOnly ? "" : "cursor-pointer hover:bg-[rgba(255,255,255,0.06)] focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-inset"}
        `}
      >
        {/* Rank Badge */}
        <div
          className={`
            flex-shrink-0 flex items-center justify-center 
            ${isHero ? "w-12 h-12 sm:w-14 sm:h-14 text-base sm:text-lg" : "w-10 h-10 sm:w-11 sm:h-11 text-sm sm:text-base"}
            rounded-lg
            font-bold
            transition-transform duration-200 group-hover:scale-105
            ${styles.badgeClass}
          `}
        >
          <span className="flex items-start">
            {styles.number}
            <sup className="text-[0.75em] ml-[0.05em] mt-[1em] font-bold">{styles.suffix}</sup>
          </span>
        </div>

        {/* Model Content */}
        {model && provider && providerInfo ? (
          <div className={`flex-1 flex items-center ${isHero ? "gap-3.5" : "gap-3"} min-w-0`}>
            {/* Provider Icon */}
            <div className="flex-shrink-0 text-foreground/90">
              <ProviderIcon provider={provider} size={isHero ? 44 : 38} />
            </div>

            {/* Model & Provider Names */}
            <div className="flex-1 min-w-0 text-left">
              <div className={`font-bold text-foreground truncate tracking-tight ${isHero ? "text-lg sm:text-xl" : "text-base sm:text-lg"}`}>
                {model}
              </div>
              <div className={`text-foreground/80 truncate uppercase tracking-wider font-bold ${isHero ? "text-sm" : "text-xs sm:text-sm"}`}>
                {providerInfo.name}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center gap-2.5">
            {/* Empty State Icon */}
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white/15 border border-white/30 flex items-center justify-center">
              <Plus className="w-4 h-4 text-white" />
            </div>

            {/* Empty State Text */}
            <span className="text-sm text-white font-medium">
              Select your {rank === 1 ? "top" : rank === 2 ? "second" : "third"}{" "}
              pick
            </span>
          </div>
        )}

        {/* Chevron Indicator - Hidden on export and in read-only mode */}
        {!isReadOnly && (
          <ChevronDown
            data-hide-on-export="true"
            className={`
              flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5
              text-white/50
              transition-transform duration-200
              ${isActive ? "rotate-180" : ""}
            `}
          />
        )}
      </div>

      {/* Blurb Input - Only shown when model is selected */}
      {model && (
        <div 
          className={`${isHero ? "px-3 sm:px-4 pb-3" : "px-2.5 sm:px-3 pb-2.5"} pt-1`}
          data-hide-on-export={!blurb ? "true" : undefined}
        >
          {/* Input for editing - hidden on export and in read-only mode */}
          {!isReadOnly && (
            <div className="flex items-center justify-center gap-1" data-hide-on-export="true">
              <span className="flex-shrink-0 font-serif text-white/50 text-2xl" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>"</span>
              <input
                type="text"
                value={blurb}
                onChange={(e) => onBlurbChange(e.target.value)}
                onBlur={handleBlurbBlur}
                placeholder={`Why ${model} ${styles.number}${styles.suffix}?`}
                maxLength={MAX_BLURB_LENGTH}
                className="flex-1 min-w-0 py-1 text-white/90 font-medium italic text-center placeholder:text-white/55 placeholder:font-medium bg-transparent border-0 border-b border-white/15 focus:border-white/40 focus:outline-none transition-colors text-base sm:text-sm"
              />
              <span className="flex-shrink-0 font-serif text-white/50 text-2xl" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>"</span>
            </div>
          )}
          {/* Static text for read-only mode and export - only visible when there's a blurb */}
          {blurb && (
            <p 
              className={`italic font-medium text-white/80 text-center text-sm ${isReadOnly ? "" : "hidden"}`}
              data-show-on-export="true"
            >
              <span className="font-serif" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>"</span>
              {blurb}
              <span className="font-serif" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>"</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

